"use client";

import { useEffect, useRef } from "react";

/**
 * Vidéo de fond en autoplay fiable sur mobile.
 *
 * Bug React connu : l'attribut `muted` n'est pas émis dans le HTML rendu
 * côté serveur (seulement posé comme propriété au montage). Safari iOS,
 * qui n'autorise l'autoplay que pour les vidéos muettes, lit le HTML sans
 * `muted` et bloque la lecture. On force donc muted + play() après montage.
 */
export default function AutoplayVideo({
  src,
  poster,
  className,
}: {
  src: string;
  poster?: string;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute("muted", "");

    // En mode économie d'énergie, iOS refuse tout autoplay MAIS autorise la
    // lecture après un geste utilisateur → on retente au premier tap/scroll.
    const tryPlayOnGesture = () => {
      v.play().catch(() => {});
      cleanup();
    };
    const cleanup = () => {
      document.removeEventListener("touchend", tryPlayOnGesture);
      document.removeEventListener("click", tryPlayOnGesture);
      document.removeEventListener("scroll", tryPlayOnGesture);
    };

    const p = v.play();
    if (p) {
      p.catch(() => {
        // Autoplay refusé (mode éco iOS…) : armer la relance sur geste
        document.addEventListener("touchend", tryPlayOnGesture, { passive: true });
        document.addEventListener("click", tryPlayOnGesture);
        document.addEventListener("scroll", tryPlayOnGesture, { passive: true });
      });
    }
    return cleanup;
  }, []);

  return (
    <video
      ref={ref}
      className={className}
      src={src}
      poster={poster}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
    />
  );
}
